import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import TertiaryButton from '@components/buttons/TertiaryButton';
import styles from '@components/carePlan/ExpertCarePlan.module.css';
import { Section } from '@components/carePlan/GoalsManager';
import ItemBrowser from '@components/carePlan/ItemBrowser';
import Tool, { ToolType } from '@components/carePlan/Tool';
import SearchInput from '@components/fieldsNative/SearchInput';
import LegalDocumentTracker from '@components/LegalDocumentTracker';
import MedicationTracker from '@components/MedicationTracker';
import ProviderTracker from '@components/ProviderTracker';
import Heading2 from '@components/typography/Heading2';
import useCreateGoalItem from '@hooks/mutations/useCreateGoalItem';
import useCreateWorksheet from '@hooks/mutations/useCreateWorksheet';
import useDestroyWorksheet from '@hooks/mutations/useDestroyWorksheet';
import useRemoveGoalItem from '@hooks/mutations/useRemoveGoalItem';
import useOpener from '@hooks/useOpener';
import useSearchWorksheetTemplates from '@hooks/useSearchWorksheetTemplates';
import { GetJourneyWithPlanQuery, GoalItemTypeEnum, GoalStatusEnum } from '@typing/Generated';
import { groupTools, medicationsBatched, providersBatched, worksheetHasChanges } from '@utils/modelUtils';

type Journey = GetJourneyWithPlanQuery['journey'];
type Goal = Journey['goals'][0];

type Props = {
  currentSection: Section;
  goal: Goal;
  journey: Journey;
};

const GoalsManagerTools = ({ currentSection, goal, journey }: Props) => {
  const toolsCreateOpener = useOpener();
  const worksheetBrowserOpener = useOpener();
  const legalOpener = useOpener();
  const medicationTrackerOpener = useOpener();
  const providerOpener = useOpener();

  const intl = useIntl();
  const [toolSearch, setToolSearch] = useState('');

  const tools = useMemo(() => {
    if (goal) {
      const groups = groupTools({
        carePlanActionItems: goal.carePlanActionItems,
        goals: [goal],
        journey,
        worksheets: goal.worksheets
      });
      return groups.new.concat(groups.inProgress).concat(groups.completed);
    }
    return [];
  }, [goal, journey]);

  const worksheetCreate = useCreateWorksheet(journey);
  const worksheetDestroy = useDestroyWorksheet(journey);

  const batchedProviders = useMemo(() => providersBatched(journey.providers), [journey.providers]);
  const batchedMedications = useMemo(() => medicationsBatched(journey.medications), [journey.medications]);

  const addGoalItem = useCreateGoalItem(goal);
  const removeGoalItem = useRemoveGoalItem(goal);

  const onToolSearch = useSearchWorksheetTemplates(
    true,
    id =>
      worksheetCreate({
        variables: {
          goalId: goal?.id ?? '',
          journeyId: journey.id,
          worksheetTemplateId: id
        }
      }),
    tool => addGoalItem({ goalId: goal?.id ?? '', itemType: GoalItemTypeEnum.TOOL, tool })
  );

  const handleToolRemove = (tool: ToolType) => {
    if (goal) {
      if (tool.worksheet) {
        worksheetDestroy({
          id: tool.worksheet.id
        });
      } else {
        removeGoalItem({
          goalId: goal.id,
          tool: tool.type
        });
      }
    }
  };

  const activeGoal = journey.goals?.find(g => g.id === goal.activeGoalId);

  return (
    <div className={styles.goalEditorSection}>
      {currentSection === Section.ALL && goal.status !== GoalStatusEnum.DRAFT && (
        <Heading2 color="primary300">
          <FormattedMessage id={`models.goal.sections.${Section.TOOLS}`} />
        </Heading2>
      )}
      <div className={styles.spaceBetween + ' ' + styles.topSpacing}>
        <div>
          <TertiaryButton
            icon={toolsCreateOpener.isOpen ? 'chevronUp' : 'chevronDown'}
            iconPosition="end"
            onClick={toolsCreateOpener.toggle}
            textKey="models.goal.tools.add"
          />
        </div>
      </div>

      <ExpandableSection isOpen={toolsCreateOpener.isOpen}>
        <div className={styles.topSpacing}>
          <SearchInput
            error=""
            initialValue={toolSearch}
            name="search"
            noSpacing
            onSearch={onToolSearch}
            placeholder={intl.formatMessage({ id: 'pages.carePlan.base.tools.searchPlaceholder' })}
            setValue={v => setToolSearch(v)}
          />
        </div>
        <ItemBrowser
          addedIds={goal.worksheets.map(w => w.worksheetTemplate.id)}
          handleAdd={id => {
            worksheetCreate({
              variables: {
                goalId: goal.id,
                journeyId: journey.id,
                worksheetTemplateId: id
              }
            });
          }}
          handleRemove={worksheetTemplateId => {
            const worksheet = goal.worksheets.find(worksheet => worksheet.worksheetTemplate.id === worksheetTemplateId);
            if (worksheet) {
              worksheetDestroy({ id: worksheet.id });
            }
          }}
          itemType={GoalItemTypeEnum.WORKSHEET}
          journeyId={journey.id}
          opener={worksheetBrowserOpener}
          skipCloseOnSubmit
        />
      </ExpandableSection>
      <div className={styles.toolItems}>
        {tools.map(tool => (
          <Tool
            archived={goal.archived}
            border={
              activeGoal?.tools.some(t => t === tool.type) ||
              (tool.worksheet && !worksheetHasChanges(tool.worksheet, activeGoal)) ||
              goal.status !== GoalStatusEnum.EDIT_MODE
                ? undefined
                : 'error300'
            }
            goal={goal}
            handleRemove={handleToolRemove}
            journeyId={journey.id}
            key={tool.type ?? tool.worksheet?.id}
            legalOpener={legalOpener}
            medicationTrackerOpener={medicationTrackerOpener}
            providerOpener={providerOpener}
            tool={tool}
          />
        ))}
      </div>

      <LegalDocumentTracker journey={journey} legalDocumentsOpener={legalOpener} />
      <ProviderTracker
        batchedProviders={batchedProviders}
        journey={journey}
        providerTrackerOpener={providerOpener}
        restartWizard
      />
      <MedicationTracker
        batchedMedications={batchedMedications}
        journey={journey}
        medicationTrackerOpener={medicationTrackerOpener}
        restartWizard
      />
    </div>
  );
};

export default GoalsManagerTools;
